import { __assign, __makeTemplateObject, __rest } from "tslib";
import * as React from 'react';
import * as PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { spacing } from '@metro-ui/tokens/js';
import { Manager, Popper, Reference } from 'react-popper';
import { CSSTransition } from 'react-transition-group';
import { forwardRef, useImperativeHandle } from 'react';
import { rem2pixelNumber } from '../utils/rem2pixels';
import { PopoverContainer, animationClass, } from './components/PopoverContainer';
import { CloseSmall } from '../icons';
import { Heading } from '../Typography';
import { IconButton } from '../Button';
var enrichHandlerWith = function (enrichF) { return function (handler) {
    if (typeof handler === 'function') {
        return function (event) {
            enrichF();
            handler(event);
        };
    }
    return enrichF;
}; };
/**
 * Hook that executes a given callback on clicks outside the passed ref
 */
var useOutsideAlerter = function (ref, setState, onClose) {
    React.useEffect(function () {
        /**
         * Alert if clicked on outside of element
         */
        function handleClickOutside(event) {
            if (ref.current &&
                ref.current.parentNode &&
                !ref.current.contains(event.target) &&
                !ref.current.parentNode.contains(event.target)) {
                event.preventDefault();
                if (setState) {
                    setState(false);
                }
                onClose === null || onClose === void 0 ? void 0 : onClose();
            }
        }
        // Bind the event listener
        document.addEventListener('mousedown', handleClickOutside);
        return function () {
            // Unbind the event listener on clean up
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [onClose, ref, setState]);
};
var PopoverHeader = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  padding: ", " ", ";\n  margin-top: 0.5rem;\n"], ["\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  padding: ", " ", ";\n  margin-top: 0.5rem;\n"])), spacing['xx-small'], spacing.small);
var StyledHeading = styled(Heading)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  margin: 0;\n"], ["\n  margin: 0;\n"])));
var CloseButton = styled(IconButton)(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  margin-left: auto;\n"], ["\n  margin-left: auto;\n"])));
export var Popover = styled(forwardRef(function (props, reference) {
    var id = props.id, title = props.title, innerComponent = props.innerComponent, innerProps = props.innerProps, _a = props.position, position = _a === void 0 ? 'top' : _a, hasFixedPosition = props.hasFixedPosition, _b = props.forwardRefAs, forwardRefAs = _b === void 0 ? 'ref' : _b, className = props.className, children = props.children, onClose = props.onClose, noCloseButton = props.noCloseButton, htmlAttributes = __rest(props, ["id", "title", "innerComponent", "innerProps", "position", "hasFixedPosition", "forwardRefAs", "className", "children", "onClose", "noCloseButton"]);
    // Open state
    var _c = React.useState(false), visible = _c[0], setVisible = _c[1];
    /**
     * Events and control methods
     */
    var showTooltip = enrichHandlerWith(function () {
        setVisible(true);
    });
    useImperativeHandle(reference, function () {
        return {
            close: function () {
                setVisible(false);
                onClose === null || onClose === void 0 ? void 0 : onClose();
            },
        };
    });
    var getEvents = function (element) {
        return {
            onClick: showTooltip(element.props.onClick),
        };
    };
    var trigger = React.Children.only(children);
    /**
     * Decorated ref to the Popover Container to detect outside clicks
     */
    var wrapperRef = React.useRef(null);
    useOutsideAlerter(wrapperRef, setVisible, onClose);
    var contentElement = null;
    if (innerComponent) {
        contentElement = innerComponent(innerProps);
    }
    return (React.createElement(Manager, null,
        React.createElement(Reference, null, function (_a) {
            var _b;
            var ref = _a.ref;
            return trigger &&
                React.cloneElement(trigger, __assign((_b = {}, _b[forwardRefAs] = ref, _b['aria-describedby'] = id, _b), getEvents(trigger)));
        }),
        React.createElement(CSSTransition, { appear: true, mountOnEnter: true, unmountOnExit: true, classNames: animationClass, timeout: 800, in: visible },
            React.createElement(Popper, { innerRef: wrapperRef, placement: position, modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: [0, rem2pixelNumber(spacing['x-small'])],
                        },
                    },
                    {
                        name: 'preventOverflow',
                        options: {
                            rootBoundary: 'viewport',
                            altAxis: true,
                        },
                    },
                ], strategy: hasFixedPosition ? 'fixed' : undefined }, function (_a) {
                var ref = _a.ref, style = _a.style, placement = _a.placement, arrowProps = _a.arrowProps;
                return (React.createElement(PopoverContainer, __assign({}, htmlAttributes, { id: id, ref: ref, role: "dialog", style: style, placement: placement, arrowProps: arrowProps, className: className }),
                    !noCloseButton || (title && title !== '') ? (React.createElement(PopoverHeader, null,
                        title && title !== '' ? (React.createElement(StyledHeading, null, title)) : null,
                        !noCloseButton ? (React.createElement(CloseButton, { Icon: CloseSmall, role: "button", label: "close-button", onClick: function () {
                                setVisible(false);
                                onClose === null || onClose === void 0 ? void 0 : onClose();
                            } })) : null)) : null,
                    contentElement));
            }))));
}))(function (_a) {
    var maxWidth = _a.maxWidth, maxHeight = _a.maxHeight;
    return css(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n    ", "\n    ", "\n  "], ["\n    ", "\n    ", "\n  "])), maxWidth && css(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n      max-width: ", "px;\n    "], ["\n      max-width: ", "px;\n    "])), maxWidth), maxHeight && css(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n      max-height: ", "px;\n    "], ["\n      max-height: ", "px;\n    "])), maxHeight));
});
Popover.propTypes = {
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    noCloseButton: PropTypes.bool,
    innerComponent: PropTypes.func,
    // eslint-disable-next-line react/forbid-prop-types
    innerProps: PropTypes.objectOf(PropTypes.any),
    maxWidth: PropTypes.number,
    maxHeight: PropTypes.number,
    onClose: PropTypes.func,
    position: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
    hasFixedPosition: PropTypes.bool,
    forwardRefAs: PropTypes.string,
    className: PropTypes.string,
    children: PropTypes.node.isRequired,
};
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6;
//# sourceMappingURL=Popover.js.map